import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Panel } from 'primereact/panel';
import { Ripple } from 'primereact/ripple';
import { Row, Col, Label } from 'reactstrap';
import { config } from 'utils/config';
import { callApi } from 'utils/utils';
import YupValidator from 'utils/YupValidator';
import FieldDropdown from 'components/Controls/FieldDropdown';
import FieldSelectButton from 'components/Controls/FieldSelectButton';
import FieldCalendar from 'components/Controls/FieldCalendar';
import FieldCheckbox from 'components/Controls/FieldCheckbox';
import FieldButton from 'components/Controls/FieldButton/FieldButton';

const ResultadoFormularioFilters = (props) => {
  const { t } = useTranslation(['formulario', 'common']);

  const [formularios, setFormularios] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [celdas, setCeldas] = useState([]);

  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  };

  const [claFormulario, setClaFormulario] = useState(null);
  const [claTurno, setClaTurno] = useState(null);
  const [claDepartamento, setClaDepartamento] = useState(null);
  const [claCelda, setClaCelda] = useState(null);
  const [fechaCapturaInicia, setFechaCapturaInicia] = useState(getDefaultStartDate);
  const [fechaCapturaFin, setFechaCapturaFin] = useState(() => new Date());
  const [esRequiereRevision, setEsRequiereRevision] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    GetFormularios();
    GetDepartments();
    GetShifts();
  }, []);

  const GetFormularios = () => {
    const urlWebService = `${config.UrlApiProject}formulario/getFormularios`;
    callApi(urlWebService, 'GET', null, (response) => {
      setFormularios(response.data.Formularios);
    });
  };

  const GetDepartments = () => {
    const urlWebService = `${config.UrlApiProject}departamento/getDepartamentos`;
    callApi(urlWebService, 'GET', null, (response) => {
      setDepartments(response.data.Departamentos);
    });
  };

  const GetShifts = () => {
    const urlWebService = `${config.UrlApiProject}turno/getTurnos`;
    callApi(urlWebService, 'GET', null, (response) => {
      setShifts(response.data.Turnos);
    });
  };

  const onFormularioChange = (e) => {
    setClaFormulario(e.value);
    setClaTurno(null);
    setClaDepartamento(null);
    setClaCelda(null);
    setCeldas([]);
  };

  const onDepartamentoChange = (e) => {
    setClaDepartamento(e.value);
    setClaCelda(null);

    if (e.value === null || e.value === undefined) {
      setCeldas([]);
      return;
    }

    const urlWebService = `${config.UrlApiProject}celda/GetCeldas?ClaDepartamento=${e.value}`;
    callApi(urlWebService, 'GET', null, (response) => {
      setCeldas(response.data.Celdas);
    });
  };

  const updateFilters = async () => {
    const validationSchema = yup.object().shape({
      claFormulario: yup.number().nullable().required(t('common:RequiredField')),
      fechaCapturaInicia: yup.date().nullable().required(t('common:RequiredField')),
      fechaCapturaFin: yup.date().nullable().required(t('common:RequiredField')),
    });

    const validator = new YupValidator(validationSchema);
    const isValid = await validator.validate({ claFormulario, fechaCapturaInicia, fechaCapturaFin });

    if (!isValid) {
      setErrors(validator.errors);
      return;
    }

    setErrors({});
    props.setFilters({
      ClaFormulario: claFormulario,
      ClaTurno: claTurno,
      ClaDepartamento: claDepartamento,
      ClaCelda: claCelda,
      FechaCapturaInicia: fechaCapturaInicia,
      FechaCapturaFin: fechaCapturaFin,
      EsRequiereRevision: esRequiereRevision,
    });
  };

  const filterHeaderTemplate = (options) => {
    const toggleIcon = options.collapsed ? 'pi pi-chevron-down text-white' : 'pi pi-chevron-up text-white';
    const className = `${options.className} p-jc-start`;
    const titleClassName = `${options.titleClassName} p-pl-1`;

    return (
      <div className="card-header">
        <div className="p-toolbar p-component">
          <div className="p-toolbar-group-left">
            <div className="card-title">
              <button type="button" className={options.togglerClassName} onClick={options.onTogglerClick}>
                <span className={toggleIcon}></span>
                <Ripple />
              </button>
              <span className={titleClassName}>
                {t('common:Filters')}
              </span>
            </div>
          </div>

          <div className="p-toolbar-group-right">
            <FieldButton className="p-button p-button-icon-only p-highlight" onClick={() => updateFilters()}>
              <i className="pi pi-search"></i>
            </FieldButton>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Panel header={t('common:Filters')} toggleable headerTemplate={filterHeaderTemplate}>
      <Row className="p-fluid p-grid">
        <Col xs={12} md={4}>
          <div className="p-field">
            <Label>{t('formulario:Questionnaire')} *</Label>
            <FieldDropdown
              name="claFormulario"
              value={claFormulario}
              options={formularios}
              optionLabel="NomFormulario"
              optionValue="ClaFormulario"
              onChange={onFormularioChange}
              errors={errors}
            />
          </div>
        </Col>

        {claFormulario === 1 || claFormulario === 2 || claFormulario === 3 || claFormulario === 4 || claFormulario === 5 ? (
          <Col xs={12} md={claFormulario === 1 ? 4 : claFormulario === 2 ? 4 : 8}>
            <div className="p-field">
              <Label>{t('formulario:Shift')}</Label>
              <FieldSelectButton
                name="claTurno"
                value={claTurno}
                options={shifts}
                optionLabel="NomTurno"
                optionValue="ClaTurno"
                onChange={(e) => setClaTurno(e.value)}
              />
            </div>
          </Col>
        ) : <></>}

        {claFormulario === 1 ? (
          <Col xs={12} md={4}>
            <div className="p-field">
              <Label>{t('formulario:Department')}</Label>
              <FieldSelectButton
                name="claDepartamento"
                value={claDepartamento}
                options={departments}
                optionLabel="NomDepartamento4"
                optionValue="ClaDepartamento4"
                onChange={onDepartamentoChange}
              />
            </div>
          </Col>
        ) : <></>}

        {claFormulario === 1 && celdas.length > 0 ? (
          <Col xs={12} md={4}>
            <div className="p-field">
              <Label>{t('formulario:Cell')}</Label>
              <FieldSelectButton
                name="claCelda"
                value={claCelda}
                options={celdas}
                optionLabel="ClaveCelda"
                optionValue="ClaCelda"
                className="cellList scrollbar-primary scroll-y"
                onChange={(e) => setClaCelda(e.value)}
              />
            </div>
          </Col>
        ) : <></>}

        <Col xs={12} md={4}>
          <div className="p-field">
            <Label>{t('common:StartDate')} *</Label>
            <FieldCalendar
              name="fechaCapturaInicia"
              value={fechaCapturaInicia}
              onChange={(e) => setFechaCapturaInicia(e.value)}
              errors={errors}
            />
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="p-field">
            <Label>{t('common:EndDate')} *</Label>
            <FieldCalendar
              name="fechaCapturaFin"
              value={fechaCapturaFin}
              onChange={(e) => setFechaCapturaFin(e.value)}
              errors={errors}
            />
          </div>
        </Col>
        <Col xs={12} md={4} className="d-flex align-items-center">
          <div className="p-field-checkbox">
            <FieldCheckbox
              inputId="EsRequiereRevision"
              checked={esRequiereRevision}
              onChange={(e) => setEsRequiereRevision(e.checked)}
            />
            <label htmlFor="EsRequiereRevision" className="ml-2">{t('formulario:ShowOnlyFlagged')}</label>
          </div>
        </Col>
      </Row>
    </Panel>
  );
};

export default ResultadoFormularioFilters;
